import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../config/env';
import { adminService } from '../services/adminService';
import type { AdminAnnouncement, AdminAnnouncementDisplayMode, AdminAnnouncementInput, AdminAnnouncementType } from '../types';

type FormState = Omit<AdminAnnouncementInput, 'startsAt' | 'endsAt'> & { startsAt: string; endsAt: string };

const emptyForm = (): FormState => ({
  type: 'NOTICE',
  title: '',
  body: '',
  imageUrl: '',
  ctaLabel: '',
  ctaUrl: '',
  displayMode: 'ON_LOGIN_ONCE',
  isActive: true,
  startsAt: '',
  endsAt: '',
  sortOrder: 0,
  reason: 'Publicacao de comunicado global',
});

const toLocalInput = (value: string | null): string => value ? new Date(value).toISOString().slice(0, 16) : '';
const resolveImageUrl = (value: string): string => value.startsWith('/') ? `${API_BASE_URL.replace(/\/api$/, '')}${value}` : value;

const AnnouncementsPage: React.FC = () => {
  const [items, setItems] = useState<AdminAnnouncement[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setItems(await adminService.listAnnouncements());
    } catch (loadError: any) {
      setError(loadError.message || 'Nao foi possivel carregar os avisos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const modeDescription = useMemo(() => ({
    INBOX_ONLY: 'Aparece apenas na caixa de entrada.',
    ON_LOGIN_ONCE: 'Abre uma vez e exige confirmacao do jogador.',
    ON_LOGIN_ALWAYS: 'Abre em toda nova entrada do jogador.',
  }[form.displayMode]), [form.displayMode]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const startEdit = (item: AdminAnnouncement) => {
    setEditingId(item.id);
    setForm({
      type: item.type,
      title: item.title,
      body: item.body,
      imageUrl: item.imageUrl || '',
      ctaLabel: item.ctaLabel || '',
      ctaUrl: item.ctaUrl || '',
      displayMode: item.displayMode,
      isActive: item.isActive,
      startsAt: toLocalInput(item.startsAt),
      endsAt: toLocalInput(item.endsAt),
      sortOrder: item.sortOrder,
      reason: 'Atualizacao de comunicado global',
    });
    setMessage('');
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const reset = () => {
    setEditingId(null);
    setForm(emptyForm());
    setError('');
    setMessage('');
  };

  const save = async () => {
    try {
      setSaving(true);
      setError('');
      setMessage('');
      const payload: AdminAnnouncementInput = {
        ...form,
        imageUrl: form.imageUrl || null,
        ctaLabel: form.ctaLabel || null,
        ctaUrl: form.ctaUrl || null,
        startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
        endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
      };
      if (editingId) await adminService.updateAnnouncement(editingId, payload);
      else await adminService.createAnnouncement(payload);
      setMessage(editingId ? 'Aviso atualizado com auditoria.' : 'Aviso publicado com auditoria.');
      reset();
      await load();
    } catch (saveError: any) {
      setError(saveError.message || 'Nao foi possivel salvar o aviso');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item: AdminAnnouncement) => {
    try {
      await adminService.updateAnnouncement(item.id, {
        isActive: !item.isActive,
        reason: item.isActive ? 'Desativacao de comunicado global' : 'Reativacao de comunicado global',
      });
      await load();
    } catch (toggleError: any) {
      setError(toggleError.message || 'Nao foi possivel alterar o aviso');
    }
  };

  const uploadImage = async (file: File | null) => {
    if (!file) return;
    try {
      setUploading(true);
      setError('');
      update('imageUrl', await adminService.uploadAnnouncementImage(file));
    } catch (uploadError: any) {
      setError(uploadError.message || 'Nao foi possivel enviar a imagem');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="admin-page-stack announcements-page">
      <section className="admin-hero-card">
        <p className="admin-section-eyebrow">Comunicacao da plataforma</p>
        <h1>Avisos</h1>
        <p>Publique comunicados na caixa de entrada ou na abertura do app. Cada visualizacao e confirmacao fica registrada por jogador.</p>
      </section>
      {error ? <div className="admin-banner admin-banner--error">{error}</div> : null}
      {message ? <div className="admin-banner">{message}</div> : null}
      <section className="admin-section-card announcements-editor">
        <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">{editingId ? 'Edicao' : 'Novo aviso'}</p><h2>{editingId ? 'Atualizar comunicado' : 'Criar comunicado'}</h2></div>{editingId ? <button type="button" className="admin-secondary-button" onClick={reset}>Cancelar edicao</button> : null}</div>
        <div className="admin-filter-grid">
          <label className="admin-detail-pair"><span>Tipo</span><select className="admin-input" value={form.type} onChange={(event) => update('type', event.target.value as AdminAnnouncementType)}><option value="INBOX">Caixa de entrada</option><option value="NOTICE">Aviso</option><option value="UPDATE">Atualizacao</option></select></label>
          <label className="admin-detail-pair"><span>Exibicao</span><select className="admin-input" value={form.displayMode} onChange={(event) => update('displayMode', event.target.value as AdminAnnouncementDisplayMode)}><option value="INBOX_ONLY">Apenas caixa de entrada</option><option value="ON_LOGIN_ONCE">Abrir uma vez</option><option value="ON_LOGIN_ALWAYS">Abrir sempre</option></select><small>{modeDescription}</small></label>
          <label className="admin-detail-pair"><span>Prioridade</span><input className="admin-input" type="number" min="-999" max="999" value={form.sortOrder} onChange={(event) => update('sortOrder', Number(event.target.value))} /></label>
          <label className="admin-detail-pair"><span>Inicio</span><input className="admin-input" type="datetime-local" value={form.startsAt} onChange={(event) => update('startsAt', event.target.value)} /></label>
          <label className="admin-detail-pair"><span>Fim</span><input className="admin-input" type="datetime-local" value={form.endsAt} onChange={(event) => update('endsAt', event.target.value)} /></label>
          <label className="admin-detail-pair announcements-editor__toggle"><span>Ativo</span><input type="checkbox" checked={form.isActive} onChange={(event) => update('isActive', event.target.checked)} /></label>
        </div>
        <label className="admin-detail-pair"><span>Titulo</span><input className="admin-input" maxLength={120} value={form.title} onChange={(event) => update('title', event.target.value)} /></label>
        <label className="admin-detail-pair"><span>Mensagem</span><textarea className="admin-input announcements-editor__textarea" maxLength={8000} value={form.body} onChange={(event) => update('body', event.target.value)} /></label>
        <div className="admin-filter-grid">
          <label className="admin-detail-pair"><span>Imagem</span><input className="admin-input" type="file" accept="image/*" onChange={(event) => void uploadImage(event.target.files?.[0] || null)} />{uploading ? <small>Processando imagem...</small> : null}</label>
          <label className="admin-detail-pair"><span>Botao</span><input className="admin-input" maxLength={80} value={form.ctaLabel || ''} onChange={(event) => update('ctaLabel', event.target.value)} /></label>
          <label className="admin-detail-pair"><span>Link do botao</span><input className="admin-input" type="url" value={form.ctaUrl || ''} onChange={(event) => update('ctaUrl', event.target.value)} /></label>
        </div>
        {form.imageUrl ? <img className="announcements-editor__preview" src={resolveImageUrl(form.imageUrl)} alt="Previa do aviso" /> : null}
        <label className="admin-detail-pair"><span>Motivo obrigatorio</span><input className="admin-input" maxLength={500} value={form.reason} onChange={(event) => update('reason', event.target.value)} /></label>
        <button className="admin-primary-button" type="button" disabled={saving || uploading || !form.title.trim() || !form.body.trim() || !form.reason.trim()} onClick={() => void save()}>{saving ? 'Salvando...' : editingId ? 'Salvar aviso' : 'Publicar aviso'}</button>
      </section>
      <section className="admin-section-card announcements-preview">
        <div className="admin-section-card__header">
          <div><p className="admin-section-eyebrow">Pre-visualizacao</p><h2>Como aparece no app</h2></div>
          <span className="announcements-preview__badge">iOS e Android</span>
        </div>
        <div className="announcements-preview__stage">
          <div className="announcements-preview__phone" aria-label="Previa do aviso no aplicativo">
            <div className="announcements-preview__notch" />
            <div className="announcements-preview__lobby" aria-hidden="true"><span>CLUBS</span><i /><i /><i /></div>
            <div className="announcements-preview__scrim" />
            <section className="announcements-preview__modal">
              <header><strong>{form.type === 'UPDATE' ? 'ATUALIZACAO' : form.type === 'INBOX' ? 'CAIXA DE ENTRADA' : 'AVISO'}</strong></header>
              <div className="announcements-preview__content">
                {form.imageUrl ? <img src={resolveImageUrl(form.imageUrl)} alt="Previa do aviso" /> : <div className="announcements-preview__image-placeholder">Imagem do aviso</div>}
                <h3>{form.title.trim() || 'Titulo do aviso'}</h3>
                <p>{form.body.trim() || 'A mensagem do aviso aparecera aqui com a mesma leitura, rolagem e hierarquia visual usadas no app.'}</p>
              </div>
              <footer className={form.ctaLabel && form.ctaUrl ? '' : 'is-single-action'}>
                {form.ctaLabel && form.ctaUrl ? <span className="announcements-preview__cta">{form.ctaLabel}</span> : null}
                <span className="announcements-preview__ok">OK</span>
              </footer>
            </section>
          </div>
        </div>
      </section>
      <section className="admin-section-card">
        <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Historico</p><h2>Comunicados publicados</h2></div></div>
        {loading ? <p>Carregando avisos...</p> : <div className="announcements-list">{items.map((item) => <article key={item.id} className={`announcements-list__item${item.isActive ? '' : ' is-inactive'}`}><div><div className="announcements-list__meta"><span>{item.type === 'INBOX' ? 'Caixa de entrada' : item.type === 'NOTICE' ? 'Aviso' : 'Atualizacao'}</span><span>{item.displayMode === 'INBOX_ONLY' ? 'Inbox' : item.displayMode === 'ON_LOGIN_ONCE' ? 'Uma vez' : 'Sempre'}</span><span>{item.isActive ? 'Ativo' : 'Inativo'}</span></div><h3>{item.title}</h3><p>{item.body}</p></div><div className="announcements-list__actions"><button type="button" className="admin-secondary-button" onClick={() => startEdit(item)}>Editar</button><button type="button" className="admin-secondary-button" onClick={() => void toggleActive(item)}>{item.isActive ? 'Desativar' : 'Ativar'}</button></div></article>)}</div>}
      </section>
    </div>
  );
};

export default AnnouncementsPage;
